'use client';

import { useEffect, useState, useRef } from 'react';

interface Position {
    x: number;
    y: number;
}

const Cursor = () => {
    // --- СОСТОЯНИЕ ---
    const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
    const [trail, setTrail] = useState<Position[]>([]);
    const [hidden, setHidden] = useState<boolean>(true);
    const [isHovering, setIsHovering] = useState<boolean>(false);
    
    // --- REFS ДЛЯ ОПТИМИЗАЦИИ ---
    const lastPositionRef = useRef<Position>({ x: 0, y: 0 });
    const movementTimerRef = useRef<NodeJS.Timeout | null>(null);
    const clearTrailTimerRef = useRef<NodeJS.Timeout | null>(null);
    const isMovingRef = useRef<boolean>(false);
    const animationFrameRef = useRef<number>(0);
    const clearIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // --- ЦВЕТА ---
    const COLORS = {
        primary: '#1c1c1c',
        hover: '#F5300D',
        secondary: '#F5300D',
        dark: '#130C0F',
        white: '#f0f0f0',
        bg: '#f0f0f0',
        surface: '#FFFFFF',
        telegram: '#24A1DE',
    };

    // --- ЛОГИКА ---
    useEffect(() => {
        const checkHoverElement = (element: Element): boolean => {
            if (!element) return false;
            
            // Проверяем интерактивные элементы
            const interactiveSelectors = [
                'a', 'button', 'input', 'textarea', 'select',
                '[role="button"]', '[onclick]', '[tabindex]',
                '.interactive', '.clickable', '.btn'
            ];
            
            // Проверяем является ли элемент или его родитель интерактивным
            let currentElement: Element | null = element;
            while (currentElement && currentElement !== document.documentElement) {
                const tagName = currentElement.tagName.toLowerCase();
                const hasInteractiveAttr = currentElement.hasAttribute('onclick') || 
                                         currentElement.hasAttribute('tabindex');
                
                if (interactiveSelectors.includes(tagName) || 
                    interactiveSelectors.some(selector => currentElement?.matches(selector)) ||
                    hasInteractiveAttr) {
                    return true;
                }
                currentElement = currentElement.parentElement;
            }
            
            return false;
        };

        const clearAllTrails = (): void => {
            setTrail([]);
        };

        const onMouseMove = (e: MouseEvent): void => {
            const newPosition = { x: e.clientX, y: e.clientY };
            setPosition(newPosition);

            // Проверяем наведение на интерактивные элементы
            const targetElement = e.target as Element;
            const hoveringInteractive = checkHoverElement(targetElement);
            setIsHovering(hoveringInteractive);

            // Если навели на интерактивный элемент - очищаем трейл
            if (hoveringInteractive) {
                clearAllTrails();
                isMovingRef.current = false;
                if (movementTimerRef.current) {
                    clearTimeout(movementTimerRef.current);
                }
                if (clearTrailTimerRef.current) {
                    clearTimeout(clearTrailTimerRef.current);
                }
                return;
            }

            // Проверяем, действительно ли курсор переместился
            const distance = Math.sqrt(
                Math.pow(newPosition.x - lastPositionRef.current.x, 2) + 
                Math.pow(newPosition.y - lastPositionRef.current.y, 2)
            );

            // Если движение значительное (больше 0.1px), обновляем трейл
            if (distance > 0.1) {
                isMovingRef.current = true;
                
                // Сбрасываем таймер полной очистки
                if (clearTrailTimerRef.current) {
                    clearTimeout(clearTrailTimerRef.current);
                    clearTrailTimerRef.current = null;
                }

                // Плавное добавление точки с requestAnimationFrame
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = requestAnimationFrame(() => {
                    setTrail(prev => {
                        const newTrail = [...prev, newPosition];
                        // СИЛЬНО УМЕНЬШИЛИ ДЛИНУ ТРЕЙЛА - всего 8 точек
                        return newTrail.slice(-8);
                    });
                });

                // Сбрасываем предыдущий таймер движения
                if (movementTimerRef.current) {
                    clearTimeout(movementTimerRef.current);
                }

                // Устанавливаем новый таймер для определения остановки
                movementTimerRef.current = setTimeout(() => {
                    isMovingRef.current = false;
                    
                    // Запускаем таймер полной очистки
                    clearTrailTimerRef.current = setTimeout(() => {
                        clearAllTrails();
                    }, 500); // 0.5 секунды
                    
                }, 100);

                lastPositionRef.current = newPosition;
            }
            
            if (window.innerWidth >= 1024) {
                setHidden(false);
            }
        };
        
        const onMouseLeave = (): void => {
            setHidden(true);
            clearAllTrails();
            setIsHovering(false);
            isMovingRef.current = false;
            if (movementTimerRef.current) {
                clearTimeout(movementTimerRef.current);
            }
            if (clearTrailTimerRef.current) {
                clearTimeout(clearTrailTimerRef.current);
            }
            cancelAnimationFrame(animationFrameRef.current);
        };
        
        const onMouseEnter = (): void => {
            setHidden(window.innerWidth < 1024);
        };

        // Функция для постепенного очищения трейла (исчезновение хвоста)
        const graduallyClearTrail = () => {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = requestAnimationFrame(() => {
                setTrail(prev => {
                    if (prev.length <= 2) return [];
                    // Удаляем по одной точке за раз для плавного исчезновения хвоста
                    return prev.slice(1);
                });
            });
        };

        // Запускаем интервал для постепенного очищения трейла когда курсор не двигается
        clearIntervalRef.current = setInterval(() => {
            if (!isMovingRef.current && trail.length > 0 && !isHovering) {
                graduallyClearTrail();
            }
        }, 32);

        // --- СЛУШАТЕЛИ ---
        const addEventListeners = (): void => {
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseenter', onMouseEnter);
            document.addEventListener('mouseleave', onMouseLeave);
        };

        const removeEventListeners = (): void => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseenter', onMouseEnter);
            document.removeEventListener('mouseleave', onMouseLeave);
        };
        
        addEventListeners();
        
        return () => {
            removeEventListeners();
            if (movementTimerRef.current) {
                clearTimeout(movementTimerRef.current);
            }
            if (clearTrailTimerRef.current) {
                clearTimeout(clearTrailTimerRef.current);
            }
            if (clearIntervalRef.current) {
                clearInterval(clearIntervalRef.current);
            }
            cancelAnimationFrame(animationFrameRef.current);
        };
    }, [trail.length, isHovering]);

    // --- УСЛОВИЯ РЕНДЕРИНГА ---
    const isMobileOrSmallScreen = typeof window !== 'undefined' && 
        (window.innerWidth < 1024 || 
         /Android|webOS|iPhone|iPad|iPod|BlackBerry/i.test(navigator.userAgent));

    if (isMobileOrSmallScreen || hidden || isHovering) {
        return null; 
    }

    // --- РИСОВАНИЕ ПРОСТОЙ КРИВОЙ БЕЗ ЭФФЕКТОВ ---
    const generatePath = () => {
        if (trail.length < 2) return null;

        const paths = [];
        
        // Создаем сегменты линии - ПРОСТЫЕ И ЧИСТЫЕ
        for (let i = 0; i < trail.length - 1; i++) {
            const startPoint = trail[i];
            const endPoint = trail[i + 1];
            
            paths.push(
                <line
                    key={i}
                    x1={startPoint.x}
                    y1={startPoint.y}
                    x2={endPoint.x}
                    y2={endPoint.y}
                    stroke={COLORS.secondary}
                    strokeWidth="10" // Тонкая линия
                    strokeLinecap="round"
                    opacity="1" // Полностью непрозрачная
                />
            );
        }
        
        return paths;
    };

    const pathSegments = generatePath();

    return (
        <svg
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 9999,
            }}
        >
            {/* Простые сегменты линии без эффектов */}
            {pathSegments}
            
            {/* Точка на конце (полностью прозрачная) */}
            <circle
                cx={position.x}
                cy={position.y}
                r="0"
                fill={COLORS.primary}
                opacity="0"
            />
        </svg>
    );
};

export default Cursor;